import React from "react";

/**
 * XpaceLogo — official brand mark (vector).
 * Variants:
 *   - "compact": X icon + "Xpace" wordmark + tagline (for navbar)
 *   - "full":    X icon + wordmark + tagline + 4 service pillars (brand block)
 * Themes:
 *   - "light": dark navy wordmark — for white/light backgrounds
 *   - "dark":  white wordmark — for dark backgrounds
 */

const COLORS = {
  navy: "#001126",
  navyDeep: "#00172F",
  blue: "#0072ff",
  blueMid: "#0047C7",
  blueLight: "#08A8FF",
};

const XMark = ({ size = 56, theme = "light" }) => {
  const w = Math.round(size * (420 / 330));
  const h = size;
  const isDark = theme === "dark";
  // On dark backgrounds the navy fills are invisible — use white instead
  const navyFill = isDark ? "#FFFFFF" : COLORS.navy;
  const accentFill = isDark ? COLORS.blueLight : COLORS.blueMid;
  const gradStart = isDark ? "#FFFFFF" : COLORS.navyDeep;
  const gradMid = isDark ? COLORS.blueLight : COLORS.blueMid;
  const gradEnd = isDark ? "#7DD8FF" : COLORS.blueLight;
  return (
    <svg width={w} height={h} viewBox="0 0 420 330" fill="none" aria-label="Xpace">
      <defs>
        <linearGradient id={`xpace-gradX-${size}-${theme}`} x1="50" y1="300" x2="320" y2="20">
          <stop offset="0%" stopColor={gradStart} />
          <stop offset="50%" stopColor={gradMid} />
          <stop offset="100%" stopColor={gradEnd} />
        </linearGradient>
      </defs>
      <path d="M70 50H145L220 130L180 170L70 50Z" fill={navyFill} />
      <path d="M320 25H390L90 300H30L320 25Z" fill={`url(#xpace-gradX-${size}-${theme})`} />
      <path d="M200 170L270 285H140L200 170Z" fill={navyFill} />
      <path d="M220 140L270 105L235 180L220 140Z" fill={navyFill} />
      <path d="M30 300L120 220L90 280L30 300Z" fill={accentFill} />
    </svg>
  );
};

const ArchitectIcon = () => (
  <svg viewBox="0 0 40 40" stroke="currentColor" fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 34V8M8 34H34" />
    <path d="M13 29L20 22L25 26L33 15" />
    <path d="M27 15H33V21" />
  </svg>
);
const CubeIcon = () => (
  <svg viewBox="0 0 40 40" stroke="currentColor" fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 4L34 12V28L20 36L6 28V12L20 4Z" />
    <path d="M6 12L20 20L34 12" />
    <path d="M20 20V36" />
  </svg>
);
const GrowthIcon = () => (
  <svg viewBox="0 0 40 40" stroke="currentColor" fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M7 32H34" />
    <path d="M10 29V22M18 29V17M26 29V12" />
    <path d="M10 18L18 12L24 15L33 6" />
    <path d="M27 6H33V12" />
  </svg>
);
const CloudIcon = () => (
  <svg viewBox="0 0 40 40" stroke="currentColor" fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M13 30H30C34 30 37 27 37 23C37 19 34 16 30 16C29 10 24 6 18 7C13 8 10 12 10 17C6 18 3 21 3 25C3 28 7 30 13 30Z" />
  </svg>
);

const PILLARS = [
  { Icon: ArchitectIcon, label: "ARCHITECT" },
  { Icon: CubeIcon, label: "IMPLEMENT" },
  { Icon: GrowthIcon, label: "TRANSFORM" },
  { Icon: CloudIcon, label: "RUN" },
];

export const XpaceLogo = ({ variant = "compact", theme = "light", className = "" }) => {
  const isDark = theme === "dark";
  const wordColor = isDark ? "#FFFFFF" : COLORS.navy;
  const lineColor = isDark ? "rgba(255,255,255,0.25)" : "#D1D5DB";
  const dividerColor = isDark ? "rgba(255,255,255,0.2)" : "#D1D5DB";
  const taglineColor = isDark ? "#E5E7EB" : COLORS.navy;
  const pillarColor = isDark ? "#FFFFFF" : COLORS.navy;
  const accent = isDark ? COLORS.blueLight : COLORS.blue;

  if (variant === "compact") {
    return (
      <div className={`flex flex-col ${className}`} data-testid="xpace-logo">
        <div className="flex items-center gap-2.5 md:gap-3">
          <XMark size={36} theme={theme} />
          <span
            className="font-display font-semibold leading-none tracking-tight text-[28px] md:text-[42px]"
            style={{ color: wordColor }}
          >
            Xpace
          </span>
        </div>
        {/* Tagline — visible on all sizes */}
        <div className="flex items-center gap-2 mt-1.5 md:mt-2 ml-[52px] md:ml-[64px]">
          <div className="w-3 md:w-4 h-px" style={{ background: lineColor }} />
          <p
            className="text-[7.5px] md:text-[9.5px] font-semibold tracking-[0.28em] md:tracking-[0.32em] whitespace-nowrap uppercase"
            style={{ color: taglineColor }}
          >
            Engineered for{" "}
            <span style={{ color: accent }}>Revenue Excellence</span>
          </p>
        </div>
        {/* Service pillars — hidden on small screens */}
        <div
          className="hidden md:flex items-center gap-3 md:gap-4 mt-2.5 ml-[64px]"
          style={{ color: pillarColor }}
        >
          {PILLARS.map(({ Icon, label }, i) => (
            <React.Fragment key={label}>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 md:w-3.5 md:h-3.5 flex-shrink-0" style={{ color: accent }}>
                  <Icon />
                </div>
                <span className="text-[8px] md:text-[9.5px] font-bold tracking-[0.18em] uppercase">
                  {label}
                </span>
              </div>
              {i < PILLARS.length - 1 && (
                <span className="h-3 w-px" style={{ background: dividerColor }} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  }

  // Full lockup
  return (
    <div className={`flex flex-col items-center text-center ${className}`} data-testid="xpace-logo-full">
      {/* X + Xpace */}
      <div className="flex items-center justify-center gap-10 md:gap-16">
        <XMark size={140} theme={theme} />
        <span
          className="font-display font-semibold leading-none tracking-[-0.04em] text-[80px] md:text-[140px]"
          style={{ color: wordColor }}
        >
          Xpace
        </span>
      </div>

      {/* Tagline with horizontal dividers */}
      <div className="flex items-center justify-center gap-6 md:gap-8 mt-8 md:mt-10">
        <div className="w-16 md:w-28" style={{ height: "2px", background: lineColor }} />
        <p
          className="text-sm md:text-[26px] font-semibold tracking-[0.35em] md:tracking-[0.45em] uppercase whitespace-nowrap"
          style={{ color: taglineColor }}
        >
          Engineered for{" "}
          <span style={{ color: accent }}>Revenue Excellence</span>
        </p>
        <div className="w-16 md:w-28" style={{ height: "2px", background: lineColor }} />
      </div>

      {/* Service pillars */}
      <div
        className="flex flex-wrap items-center justify-center gap-6 md:gap-10 mt-12 md:mt-16"
        style={{ color: pillarColor }}
      >
        {PILLARS.map(({ Icon, label }, i) => (
          <React.Fragment key={label}>
            <div className="flex items-center gap-3 md:gap-5">
              <div
                className="w-7 h-7 md:w-10 md:h-10 flex-shrink-0"
                style={{ color: accent }}
              >
                <Icon />
              </div>
              <div className="text-sm md:text-[22px] font-bold tracking-[0.22em] md:tracking-[0.25em] uppercase">
                {label}
              </div>
            </div>
            {i < PILLARS.length - 1 && (
              <div className="h-8 md:h-12" style={{ width: "2px", background: dividerColor }} />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default XpaceLogo;
